import { create } from 'zustand'

import {
  type CoreItem,
  type CoreProductPhotoPayload,
  type CoreBundleItemPayload,
  type CoreBundle,
} from '@flecto/types'
import { deepClone, fileToBase64, uploadImageByBase64 } from '@flecto/utils'

import { useBundleGeneratorAI } from '../../hooks/useBundleGeneratorAI'
import { useItemGeneratorAI } from '../../hooks/useItemGeneratorAI'
import { type ItemPhotoForManager } from './GridItemPhotosManager'

/**
 * This grid item photos state is based on 2 objects: the given item.photos
 * (CoreItemPhoto[]) and a calculated item.photos_attributes (CoreItemPhotoPayload[]),
 * always based on initial item.photos.
 */
export type GridItemPhotoManagerState = {
  photos: ItemPhotoForManager[]
  photosAttributes: CoreProductPhotoPayload[]

  deletePhoto: (index: number) => void
  uploadPhoto: (file: File, index: number) => void
  generateListPhotosWithAI: (
    param: string | Array<CoreBundleItemPayload>
  ) => Promise<void>
  generateSinglePhotoWithAI: (
    param: string | Array<CoreBundleItemPayload>,
    index: number
  ) => Promise<void>
  initializeFromItem: (product?: CoreItem | CoreBundle) => void
}

const emptyPhoto: ItemPhotoForManager = {
  main: false,
  order: 0,
  photo_image_id: '',
  links: { photo_url: '' },
  _destroy: false,
  photo_url: '',
}

export const GRID_SIZE = 7

export const useGridItemPhotosManagerState = create<GridItemPhotoManagerState>(
  (set, get) => ({
    photos: Array(7).fill(deepClone(emptyPhoto)),
    photosAttributes: [],

    initializeFromItem: (product?: CoreItem | CoreBundle) => {
      const newPhotos: ItemPhotoForManager[] = []
      const newPhotosPayload: CoreProductPhotoPayload[] = []

      get().photos.forEach((_p, index) => {
        const photo = deepClone(emptyPhoto)
        const existingPhoto = product?.photos?.[index]

        photo.order = index
        photo.main = index === 0 ? true : false

        if (existingPhoto) {
          photo.id = Number(existingPhoto.id)
          photo.links = { photo_url: existingPhoto.photo_url }

          const photoPayload: CoreProductPhotoPayload = {
            id: photo.id,
            _destroy: false,
            main: photo.main,
            order: photo.order,
          }

          newPhotosPayload.push(photoPayload)
        }

        newPhotos.push(photo)
      })

      set({
        photos: newPhotos,
        photosAttributes: newPhotosPayload,
      })
    },

    deletePhoto: (index: number) => {
      const photos = get().photos

      if (photos[index]) {
        photos[index]._destroy = true
        photos[index].links.photo_url = ''
      }

      const photosAttributes = get().photosAttributes.map((pa) => {
        if (pa.order === index) {
          pa._destroy = true

          if (pa.photo_image_id) {
            pa.photo_image_id = ''
          }
        }

        return pa
      })

      set({
        photos,
        photosAttributes: [
          ...photosAttributes.filter(
            (pa) => pa && (pa.id || pa.photo_image_id)
          ),
        ],
      })
    },

    uploadPhoto: async (file: File, index: number) => {
      try {
        useItemGeneratorAI.getState().setIsLoadingImageSingle(index, true)
        const base64 = await fileToBase64(file)
        const { id, urlTemporary } = await uploadImageByBase64(base64)

        const photos = get().photos
        const photosAttributes = get().photosAttributes

        if (id && urlTemporary) {
          photos[index].links.photo_url = urlTemporary

          if (!photosAttributes[index]) {
            photosAttributes[index] = {}
          }

          photosAttributes[index].photo_image_id = id
          photosAttributes[index].order = index
          photosAttributes[index]._destroy = false
          photosAttributes[index].main = index === 0 ? true : false
        }

        set({
          photos,
          photosAttributes: [
            ...photosAttributes.filter(
              (pa) => pa && (pa.id || pa.photo_image_id)
            ),
          ],
        })
      } finally {
        useItemGeneratorAI.getState().setIsLoadingImageSingle(index, false)
      }
    },

    generateListPhotosWithAI: async (
      param: string | Array<CoreBundleItemPayload>
    ) => {
      try {
        useItemGeneratorAI.setState({ isLoadingImageList: true })
        let imagesResults

        if (typeof param === 'string') {
          const { getImageListWithAI } = useItemGeneratorAI.getState()
          imagesResults = await getImageListWithAI(param)
        } else {
          const { getImageListWithAI } = useBundleGeneratorAI.getState()
          imagesResults = await getImageListWithAI(param)
        }

        const photos = get().photos
        const photosAttributes = get().photosAttributes

        imagesResults.forEach((imageUploaded, index) => {
          const { id, urlTemporary } = imageUploaded

          if (id && urlTemporary) {
            photos[index].links.photo_url = urlTemporary

            if (!photosAttributes[index]) {
              photosAttributes[index] = {}
            }

            photosAttributes[index].photo_image_id = id
            photosAttributes[index].order = index
            photosAttributes[index]._destroy = false
            photosAttributes[index].main = index === 0 ? true : false
          }
        })

        set({
          photos,
          photosAttributes: [
            ...photosAttributes.filter(
              (pa) => pa && (pa.id || pa.photo_image_id)
            ),
          ],
        })
      } catch (e) {
        console.error('Error generating list images with AI.', e)
      } finally {
        useItemGeneratorAI.setState({ isLoadingImageList: false })
      }
    },

    generateSinglePhotoWithAI: async (
      param: string | Array<CoreBundleItemPayload>,
      index: number
    ) => {
      try {
        useItemGeneratorAI.getState().setIsLoadingImageSingle(index, true)
        let id: string | undefined
        let urlTemporary: string | undefined

        if (typeof param === 'string') {
          const { getImageWithAI } = useItemGeneratorAI.getState()
          const result = await getImageWithAI(param, index)
          id = result.id
          urlTemporary = result.urlTemporary
        } else {
          const { getImageWithAI } = useBundleGeneratorAI.getState()
          const result = await getImageWithAI(param, index)
          id = result.id
          urlTemporary = result.urlTemporary
        }

        if (id && urlTemporary) {
          const photos = get().photos
          const photosAttributes = get().photosAttributes
          const existingPhotoPayload = photosAttributes.find(
            (pa) => pa.order === index
          )

          photos[index].links.photo_url = urlTemporary

          if (!existingPhotoPayload) {
            photosAttributes.push({
              photo_image_id: id,
              main: false,
              order: index,
              _destroy: false,
            })
          } else {
            existingPhotoPayload.photo_image_id = id
            existingPhotoPayload.order = index
            existingPhotoPayload._destroy = false
          }

          set({
            photos,
            photosAttributes: [
              ...photosAttributes.filter(
                (pa) => pa && (pa.id || pa.photo_image_id)
              ),
            ],
          })
        }
      } catch (e) {
        console.error('Error generating single image with AI.', e)
      } finally {
        useItemGeneratorAI.getState().setIsLoadingImageSingle(index, false)
      }
    },
  })
)
